import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * MetricCard Variants
 *
 * - `danger`: Critical/high-risk metrics (destructive color)
 * - `info`: Primary KPIs and activity metrics (accent color)
 * - `neutral`: Secondary/general information (muted color)
 * - `success`: Positive/clearance metrics (green)
 */
export type MetricCardVariant = 'danger' | 'info' | 'neutral' | 'success';

export interface MetricCardProps {
  /** The variant determines the accent color and semantic meaning */
  variant: MetricCardVariant;
  /** The label describing the metric */
  label: string;
  /** The numeric value to display */
  value: number | string;
  /** Optional formatted display value (e.g., "1.2 GB" instead of raw bytes) */
  displayValue?: string;
  /** Icon component (Lucide icon) */
  icon: React.ComponentType<{ className?: string }>;
  /** Optional click handler - makes the card interactive */
  onClick?: () => void;
  /** Additional className for the outer card */
  className?: string;
  /** Whether to show the value in a muted state when zero */
  muteWhenZero?: boolean;
}

/**
 * Variant styles using system design tokens
 */
const variantStyles: Record<
  MetricCardVariant,
  {
    border: string;
    iconWell: string;
    icon: string;
    value: string;
  }
> = {
  danger: {
    border: 'border-t-destructive',
    iconWell: 'bg-destructive/10',
    icon: 'text-destructive',
    value: 'text-destructive',
  },
  info: {
    border: 'border-t-accent-foreground',
    iconWell: 'bg-accent',
    icon: 'text-accent-foreground',
    value: 'text-foreground',
  },
  neutral: {
    border: 'border-t-muted-foreground',
    iconWell: 'bg-muted',
    icon: 'text-muted-foreground',
    value: 'text-foreground',
  },
  success: {
    border: 'border-t-green-500',
    iconWell: 'bg-green-500/10',
    icon: 'text-green-500',
    value: 'text-green-600 dark:text-green-400',
  },
};

/**
 * MetricCard - Simple stat card for Shadow AI metrics
 */
export function MetricCard({
  variant,
  label,
  value,
  displayValue,
  icon: Icon,
  onClick,
  className,
  muteWhenZero = false,
}: MetricCardProps): React.ReactElement {
  const isClickable = !!onClick;
  const isZero = value === 0 || value === '0';
  const shouldMute = muteWhenZero && isZero;
  const styles = variantStyles[variant];

  return (
    <div
      className={cn(
        // Base card styles
        'rounded-lg p-5',
        // Background
        'bg-card',
        // Border - 3px top accent, 1px elsewhere
        'border border-border',
        'border-t-[3px]',
        styles.border,
        // Shadow
        'shadow-sm',
        // Interactive states
        isClickable && [
          'cursor-pointer',
          'hover:border-accent-foreground/50',
          'hover:border-t-accent-foreground',
          'transition-colors',
        ],
        className
      )}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      {/* Icon Row */}
      <div className="flex items-center justify-between">
        <div
          className={cn('p-2 rounded-lg', styles.iconWell, {
            'opacity-50': shouldMute,
          })}
        >
          <Icon
            className={cn('w-5 h-5', styles.icon, {
              'opacity-50': shouldMute,
            })}
          />
        </div>
        {isClickable && !isZero && (
          <span className="text-xs text-muted-foreground">→</span>
        )}
      </div>

      {/* Value */}
      <div
        className={cn(
          'text-2xl font-bold leading-tight mt-3',
          shouldMute ? 'text-muted-foreground' : styles.value
        )}
      >
        {displayValue ?? value}
      </div>

      {/* Label */}
      <div className="text-xs font-medium mt-1 text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
